import React, { useState, useEffect } from "react";
import { Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { downloadSignedPdf, downloadCertificatePdf, downloadBundle, downloadZip } from "../../api";

const CheckRow = ({ checked, onToggle, label, count, indent, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onToggle}
    className={`flex w-full items-center gap-3 text-left ${indent ? "pl-7" : ""}`}
  >
    <span
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border-2 transition-colors ${
        checked ? "border-[#7A6FF0] bg-[#7A6FF0]" : "border-[#C9C2BA] dark:border-white/25"
      }`}
    >
      {checked && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} />}
    </span>
    <span className="text-sm font-medium text-[#1A1815] dark:text-white">{label}</span>
    {count != null && (
      <span className="text-sm text-[#8B8680] dark:text-white/45">({count})</span>
    )}
  </button>
);

const DownloadFilesModal = ({ open, onClose, docId, baseName, onAction, docCount = 1 }) => {
  const DOC_COUNT = Math.max(1, docCount);
  const CERT_COUNT = 1;
  const [selDoc, setSelDoc] = useState(true);
  const [selCert, setSelCert] = useState(true);
  const [combine, setCombine] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSelDoc(true);
      setSelCert(true);
      setCombine(false);
      setBusy(false);
    }
  }, [open]);

  const all = selDoc && selCert;
  const toggleAll = () => {
    const next = !all;
    setSelDoc(next);
    setSelCert(next);
  };
  const totalCount = DOC_COUNT + CERT_COUNT;

  const doDownload = async () => {
    if (busy || (!selDoc && !selCert)) return;
    setBusy(true);
    try {
      const include = [];
      if (selDoc) include.push("document");
      if (selCert) include.push("certificate");
      const fileCount = (selDoc ? DOC_COUNT : 0) + (selCert ? 1 : 0);
      if (combine && include.length > 1) {
        // Merge all selected files' pages into a single PDF.
        await downloadBundle(docId, include, `${baseName}-combined.pdf`);
      } else if (!combine && fileCount > 1) {
        // Bundle multiple separate files into one .zip (no page merging).
        await downloadZip(docId, include, `${baseName}-files.zip`);
      } else {
        // Exactly one file selected → download it directly.
        if (selDoc) {
          await downloadSignedPdf(docId, `${baseName}-signed.pdf`);
        } else if (selCert) {
          await downloadCertificatePdf(docId, `${baseName}-certificate.pdf`);
        }
      }
      onClose();
    } catch (e) {
      onAction("Could not download the selected files");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="download-files-modal" className="z-[60] sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="text-left">Download files</DialogTitle>
          <DialogDescription className="text-left">
            Select which files you want to download:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5 pt-1">
          <CheckRow testid="dl-all" checked={all} onToggle={toggleAll} label="All" count={totalCount} />
          <CheckRow testid="dl-document" checked={selDoc} onToggle={() => setSelDoc((v) => !v)} label="Document" count={DOC_COUNT} indent />
          <CheckRow testid="dl-certificate" checked={selCert} onToggle={() => setSelCert((v) => !v)} label="Certificate of Completion" count={CERT_COUNT} indent />
          <div className="border-t border-[#E7E0D8] dark:border-white/10" />
          <CheckRow testid="dl-combine" checked={combine} onToggle={() => setCombine((v) => !v)} label="Combine all PDFs into a single file" />
        </div>
        <div className="mt-4 flex items-center justify-end">
          <button
            data-testid="dl-download-btn"
            onClick={doDownload}
            disabled={busy || (!selDoc && !selCert)}
            className="rounded-xl bg-[#1A1815] px-7 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 dark:bg-white dark:text-black"
          >
            {busy ? "Preparing…" : "Download"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadFilesModal;
